// Check #2: returns [{ entity, id, type, reasons }] for lanelets whose left/right IDs
// are NOT found in other viewer dataSources (excluding currentDataSource).
// Reasons are only: "Left ID not found" / "Right ID not found".
export function getLaneletsWithMissingSideIds(viewer, currentDataSource) {
  if (!currentDataSource || !currentDataSource.entities) return [];
  if (!viewer || !viewer.dataSources) return [];

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue() : property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue();
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;
    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function buildAllOtherIdsSet(viewer, currentDataSource) {
    const ids = new Set();
    const collection = viewer.dataSources;

    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || ds === currentDataSource || !ds.entities) continue;

      const ents = ds.entities.values || ds.entities;
      if (!Array.isArray(ents)) continue;

      for (let j = 0; j < ents.length; j++) {
        const e = ents[j];
        if (!e || !e.properties) continue;

        const idValue = getPropertyValueFromProps(e.properties, "id");
        if (idValue === null || idValue === undefined || idValue === "") continue;

        ids.add(String(idValue));
      }
    }

    return ids;
  }

  const otherIds = buildAllOtherIdsSet(viewer, currentDataSource);

  const lanelets = currentDataSource.entities.values || currentDataSource.entities;
  if (!Array.isArray(lanelets) || lanelets.length === 0) return [];

  const result = [];
  const seenIds = new Set();

  for (let i = 0; i < lanelets.length; i++) {
    const laneletEntity = lanelets[i];
    if (!laneletEntity || !laneletEntity.properties) continue;

    const props = laneletEntity.properties;

    const leftId = getPropertyValueFromProps(props, "left_line_id");
    const rightId = getPropertyValueFromProps(props, "right_line_id");

    const reasons = [];

    const hasLeftId = leftId !== null && leftId !== undefined && String(leftId) !== "";
    const hasRightId = rightId !== null && rightId !== undefined && String(rightId) !== "";

    if (hasLeftId && !otherIds.has(String(leftId))) reasons.push("Left ID not found");
    if (hasRightId && !otherIds.has(String(rightId))) reasons.push("Right ID not found");

    if (reasons.length > 0) {
      const id = getEntityId(laneletEntity);
      const dedupeKey = id ?? laneletEntity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({
          entity: laneletEntity,
          id,
          type: "lanelet_missing_sides",
          reasons,
        });
      }
    }
  }

  return result;
}
